import { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { ArrowRightLeft, Plus, Loader2, CheckCircle, XCircle, Clock, Warehouse, GitBranch, Package } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { format } from "date-fns";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

interface Branch {
  id: number;
  name: string;
  type: "warehouse" | "branch";
}

interface Product {
  id: number;
  name: string;
  sku: string;
  stock: number;
  branchId: number | null;
}

interface Transfer {
  id: number;
  fromBranchId: number;
  fromBranchName: string;
  fromBranchType: string;
  toBranchId: number;
  toBranchName: string;
  toBranchType: string;
  productId: number;
  productName: string;
  productSku: string;
  quantity: number;
  notes: string | null;
  status: "pending" | "completed" | "cancelled";
  createdAt: string;
  completedAt: string | null;
}

const STATUS_STYLES: Record<string, { label: string; className: string; icon: React.FC<any> }> = {
  pending: { label: "Pending", className: "bg-amber-100 text-amber-800", icon: Clock },
  completed: { label: "Completed", className: "bg-emerald-100 text-emerald-800", icon: CheckCircle },
  cancelled: { label: "Cancelled", className: "bg-red-100 text-red-800", icon: XCircle },
};

const EMPTY_FORM = { fromBranchId: "", toBranchId: "", productId: "", quantity: "", notes: "" };

export default function StockTransfers() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location] = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState<number | null>(null);
  const [branchProducts, setBranchProducts] = useState<Product[]>([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    if (location.includes("add=1")) setIsOpen(true);
  }, [location]);

  const { data: transfers = [], isLoading } = useQuery<Transfer[]>({
    queryKey: ["stock-transfers"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/stock-transfers`, { credentials: "include" });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error ?? "Failed to load transfers");
      }
      return res.json();
    },
  });

  const { data: branches = [] } = useQuery<Branch[]>({
    queryKey: ["branches"],
    queryFn: async () => {
      const res = await fetch(`${BASE}/api/branches`, { credentials: "include" });
      if (!res.ok) return [];
      return res.json();
    },
  });

  async function loadProductsForBranch(branchId: string) {
    if (!branchId) { setBranchProducts([]); return; }
    setLoadingProducts(true);
    try {
      const res = await fetch(`${BASE}/api/branches/${branchId}/inventory`, { credentials: "include" });
      if (res.ok) setBranchProducts(await res.json());
    } finally {
      setLoadingProducts(false);
    }
  }

  async function handleSave() {
    if (!form.fromBranchId || !form.toBranchId || !form.productId || !form.quantity) {
      toast({ title: "All fields are required", variant: "destructive" });
      return;
    }
    if (Number(form.quantity) <= 0) {
      toast({ title: "Quantity must be greater than zero", variant: "destructive" });
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BASE}/api/stock-transfers`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          fromBranchId: Number(form.fromBranchId),
          toBranchId: Number(form.toBranchId),
          productId: Number(form.productId),
          quantity: Number(form.quantity),
          notes: form.notes || undefined,
        }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to create transfer");
      toast({ title: "Transfer initiated", description: "Stock deducted from source. Mark as completed when received." });
      queryClient.invalidateQueries({ queryKey: ["stock-transfers"] });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
      setIsOpen(false);
      setForm(EMPTY_FORM);
      setBranchProducts([]);
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function updateStatus(id: number, status: "completed" | "cancelled") {
    setUpdating(id);
    try {
      const res = await fetch(`${BASE}/api/stock-transfers/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ status }),
      });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error ?? "Failed to update");
      toast({ title: status === "completed" ? "Transfer completed — stock added to destination" : "Transfer cancelled — stock restored" });
      queryClient.invalidateQueries({ queryKey: ["stock-transfers"] });
      queryClient.invalidateQueries({ queryKey: ["branches"] });
    } catch (e: any) {
      toast({ title: e.message, variant: "destructive" });
    } finally {
      setUpdating(null);
    }
  }

  const pending = transfers.filter(t => t.status === "pending");
  const history = transfers.filter(t => t.status !== "pending");

  return (
    <>
    <div className="p-6 space-y-6 max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <ArrowRightLeft className="w-6 h-6 text-primary" /> Stock Transfers
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Move stock between your warehouse and branches, or between branches.
            </p>
          </div>
          <Button onClick={() => { setForm(EMPTY_FORM); setBranchProducts([]); setIsOpen(true); }}>
            <Plus className="w-4 h-4 mr-1" /> New Transfer
          </Button>
        </div>

        {isLoading && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && (
          <>
            {/* Pending transfers */}
            {pending.length > 0 && (
              <div>
                <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">
                  Pending Transfers ({pending.length})
                </h2>
                <div className="space-y-3">
                  {pending.map(t => (
                    <Card key={t.id} className="border-amber-200 bg-amber-50/50">
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <Package className="w-4 h-4 text-muted-foreground shrink-0" />
                              <span className="font-medium text-sm">{t.productName}</span>
                              <span className="text-xs text-muted-foreground">({t.productSku})</span>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <BranchPill name={t.fromBranchName} type={t.fromBranchType} />
                              <ArrowRightLeft className="w-3.5 h-3.5 shrink-0" />
                              <BranchPill name={t.toBranchName} type={t.toBranchType} />
                              <span className="ml-auto font-semibold text-foreground">Qty: {t.quantity}</span>
                            </div>
                            {t.notes && <p className="text-xs text-muted-foreground mt-1 italic">{t.notes}</p>}
                            <p className="text-xs text-muted-foreground mt-1">{format(new Date(t.createdAt), "dd MMM yyyy, h:mm a")}</p>
                          </div>
                          <div className="flex gap-2 shrink-0">
                            <Button size="sm" variant="outline"
                              className="text-destructive hover:text-destructive border-destructive/30"
                              onClick={() => updateStatus(t.id, "cancelled")}
                              disabled={updating === t.id}>
                              {updating === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <XCircle className="w-3.5 h-3.5 mr-1" />}
                              Cancel
                            </Button>
                            <Button size="sm" onClick={() => updateStatus(t.id, "completed")} disabled={updating === t.id}>
                              {updating === t.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle className="w-3.5 h-3.5 mr-1" />}
                              Mark Received
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Transfer history */}
            <div>
              <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide mb-3">Transfer History</h2>
              {history.length === 0 && pending.length === 0 ? (
                <div className="text-center py-16 border rounded-xl bg-muted/20">
                  <ArrowRightLeft className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="font-medium mb-1">No transfers yet</p>
                  <p className="text-muted-foreground text-sm">Start by creating a new transfer above.</p>
                </div>
              ) : history.length === 0 ? null : (
                <div className="border rounded-xl overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Product</TableHead>
                        <TableHead>From</TableHead>
                        <TableHead>To</TableHead>
                        <TableHead className="text-center">Qty</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {history.map(t => {
                        const s = STATUS_STYLES[t.status];
                        const Icon = s.icon;
                        return (
                          <TableRow key={t.id}>
                            <TableCell>
                              <div className="font-medium text-sm">{t.productName}</div>
                              <div className="text-xs text-muted-foreground">{t.productSku}</div>
                            </TableCell>
                            <TableCell><BranchPill name={t.fromBranchName} type={t.fromBranchType} /></TableCell>
                            <TableCell><BranchPill name={t.toBranchName} type={t.toBranchType} /></TableCell>
                            <TableCell className="text-center font-medium">{t.quantity}</TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${s.className}`}>
                                <Icon className="w-3 h-3" /> {s.label}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {format(new Date(t.createdAt), "dd MMM yyyy")}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* New Transfer Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>New Stock Transfer</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>From (Source)</Label>
              <Select value={form.fromBranchId} onValueChange={v => {
                setForm(f => ({ ...f, fromBranchId: v, productId: "" }));
                loadProductsForBranch(v);
              }}>
                <SelectTrigger><SelectValue placeholder="Select source branch" /></SelectTrigger>
                <SelectContent>
                  {branches.map(b => (
                    <SelectItem key={b.id} value={String(b.id)}>
                      {b.type === "warehouse" ? "🏭" : "🏪"} {b.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>To (Destination)</Label>
              <Select value={form.toBranchId} onValueChange={v => setForm(f => ({ ...f, toBranchId: v }))}>
                <SelectTrigger><SelectValue placeholder="Select destination branch" /></SelectTrigger>
                <SelectContent>
                  {branches
                    .filter(b => String(b.id) !== form.fromBranchId)
                    .map(b => (
                      <SelectItem key={b.id} value={String(b.id)}>
                        {b.type === "warehouse" ? "🏭" : "🏪"} {b.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label>Product</Label>
              {loadingProducts ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading products...
                </div>
              ) : (
                <Select value={form.productId} onValueChange={v => setForm(f => ({ ...f, productId: v }))}
                  disabled={!form.fromBranchId || branchProducts.length === 0}>
                  <SelectTrigger>
                    <SelectValue placeholder={!form.fromBranchId ? "Select source first" : branchProducts.length === 0 ? "No products in this branch" : "Select product"} />
                  </SelectTrigger>
                  <SelectContent>
                    {branchProducts.map(p => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name} — Stock: {p.stock}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-1.5">
              <Label>Quantity</Label>
              <Input type="number" min={1} placeholder="e.g. 50"
                value={form.quantity} onChange={e => setForm(f => ({ ...f, quantity: e.target.value }))} />
            </div>

            <div className="space-y-1.5">
              <Label>Notes (optional)</Label>
              <Textarea placeholder="Reason for transfer, reference number, etc."
                value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                className="resize-none" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
              Initiate Transfer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BranchPill({ name, type }: { name: string; type: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-0.5 rounded-full font-medium">
      {type === "warehouse" ? <Warehouse className="w-3 h-3" /> : <GitBranch className="w-3 h-3" />}
      {name}
    </span>
  );
}
